import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/* ─── Recruitment Hero Background ─── */
const RECRUIT_HERO_BG = 'https://readdy.ai/api/search-image?query=Elegant%20abstract%20golden%20ascending%20stairs%20and%20upward%20light%20beams%20on%20deep%20navy%20blue%20background%20minimal%20sophisticated%20high-end%20corporate%20aesthetic%20soft%20glowing%20particles%20luxurious%20atmosphere%20no%20text&width=1920&height=800&seq=recruithero1&orientation=landscape';

/* ─── Office Image ─── */
const OFFICE_IMG = 'https://readdy.ai/api/search-image?query=Modern%20elegant%20Japanese%20corporate%20office%20workspace%20with%20warm%20natural%20lighting%20sophisticated%20minimalist%20interior%20design%20wooden%20accents%20clean%20professional%20atmosphere%20Tokyo%20Marunouchi%20style%20high-end%20no%20people&width=900&height=600&seq=office1&orientation=landscape';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '@/utils/seo';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import ElegantHeader from '@/components/feature/ElegantHeader';
import ElegantFooter from '@/components/feature/ElegantFooter';
import ElegantHero from '@/components/feature/ElegantHero';
import ElegantSectionTitle from '@/components/feature/ElegantSectionTitle';

export default function RecruitmentPage() {
  const { t, i18n } = useTranslation();

  const getSchemaLang = () => {
    const map: Record<string, string> = { ja: 'ja-JP', en: 'en-US', ko: 'ko-KR', 'zh-TW': 'zh-TW' };
    return map[i18n.language] || 'ja-JP';
  };

  const { ref: whyRef, visible: whyVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: positionsRef, visible: positionsVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: processRef, visible: processVisible } = useScrollReveal({ threshold: 0.1 });

  useSEO({
    title: t('seo.recruitmentTitle'),
    description: t('seo.recruitmentDescription'),
    keywords: t('seo.recruitmentKeywords'),
    path: '/recruitment',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(t('seo.recruitmentTitle'), t('seo.recruitmentDescription'), '/recruitment', getSchemaLang()),
        generateBreadcrumbSchema([
          { name: t('nav.topPage'), path: '/' },
          { name: t('recruitment.title'), path: '/recruitment' },
        ]),
      ],
    },
  });

  const positions = [
    { title: 'Marketing Consultant', type: 'Full-time', location: 'Tokyo', description: 'Lead strategic marketing initiatives for enterprise clients' },
    { title: 'Data Analyst', type: 'Full-time', location: 'Tokyo', description: 'Transform complex data into actionable business insights' },
    { title: 'Web Developer', type: 'Full-time', location: 'Tokyo / Remote', description: 'Build cutting-edge web solutions for diverse clients' },
    { title: 'Digital Marketing Specialist', type: 'Full-time', location: 'Tokyo', description: 'Execute and optimize digital advertising campaigns' },
  ];

  const reasons = [
    { title: 'Growth & Development', desc: 'We invest in our people. From continuous training programs to mentorship opportunities, we provide the resources you need to advance your career and expand your expertise.' },
    { title: 'Innovative Projects', desc: 'Work on cutting-edge projects with leading brands and organizations. Every day brings new challenges and opportunities to make a real impact.' },
    { title: 'Collaborative Culture', desc: 'Join a team of passionate professionals who value collaboration, creativity, and mutual support. We succeed together.' },
    { title: 'Work-Life Balance', desc: 'We believe in sustainable success. Flexible work arrangements and a supportive environment help you thrive both professionally and personally.' },
  ];

  const steps = [
    { title: 'Submit Application', desc: 'Send us your resume and cover letter through our contact form' },
    { title: 'Initial Screening', desc: 'Our HR team will review your application and reach out if there is a match' },
    { title: 'Interviews', desc: 'Meet with team members and leadership to discuss the role and your fit' },
    { title: 'Offer & Onboarding', desc: 'Receive your offer and begin your journey with TradFit' },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#FAF9F6' }}>
      <ElegantHeader />

      <ElegantHero
        title={t('recruitment.title')}
        subtitle={t('recruitment.subtitle')}
        tag="Careers"
        backgroundImage={RECRUIT_HERO_BG}
        breadcrumb={[
          { label: t('nav.topPage'), to: '/' },
          { label: t('recruitment.title') },
        ]}
      />

      <main className="py-20 md:py-28 px-6 md:px-10 max-w-[1400px] mx-auto space-y-24 md:space-y-32">
        {/* Office Image Section */}
        <section>
          <div className="relative overflow-hidden" style={{ aspectRatio: '21/9' }}>
            <img
              src={OFFICE_IMG}
              alt="TradFit Office"
              className="w-full h-full object-cover"
            />
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(180deg, rgba(10,25,47,0.3) 0%, rgba(10,25,47,0.1) 40%, rgba(10,25,47,0.4) 100%)',
              }}
            />
            {/* Gold frame accents */}
            <div
              className="absolute top-6 left-6 w-16 h-16"
              style={{
                borderTop: '1px solid rgba(201,162,39,0.5)',
                borderLeft: '1px solid rgba(201,162,39,0.5)',
              }}
            />
            <div
              className="absolute bottom-6 right-6 w-16 h-16"
              style={{
                borderBottom: '1px solid rgba(201,162,39,0.5)',
                borderRight: '1px solid rgba(201,162,39,0.5)',
              }}
            />
            {/* Center text overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center px-6">
                <p
                  className="text-xs tracking-widest-plus uppercase mb-3"
                  style={{
                    color: '#C9A227',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 500,
                  }}
                >
                  {t('recruitment.ourWorkplace')}
                </p>
                <h2
                  className="text-2xl md:text-3xl lg:text-4xl"
                  style={{
                    fontFamily: "'Noto Serif JP', 'Playfair Display', serif",
                    fontWeight: 500,
                    color: '#FFFFFF',
                    letterSpacing: '0.04em',
                    textShadow: '0 2px 20px rgba(10,25,47,0.5)',
                  }}
                >
                  {t('recruitment.creativityEnvironment')}
                </h2>
              </div>
            </div>
          </div>
        </section>

        {/* Why TradFit */}
        <section ref={whyRef as React.RefObject<HTMLElement>}>
          <div className="mb-16">
            <ElegantSectionTitle
              tag="Why TradFit"
              title={t('recruitment.whyJoinUs')}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {reasons.map((r, i) => (
              <div
                key={i}
                className="p-6 md:p-8 border transition-all duration-300"
                style={{
                  background: '#FFFFFF',
                  borderColor: 'rgba(212, 207, 196, 0.4)',
                  opacity: whyVisible ? 1 : 0,
                  transform: whyVisible ? 'translateY(0)' : 'translateY(24px)',
                  transition: `opacity 0.7s ease ${0.08 + i * 0.07}s, transform 0.7s ease ${0.08 + i * 0.07}s, border-color 0.3s`,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(201, 162, 39, 0.3)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(212, 207, 196, 0.4)';
                }}
              >
                <h3
                  className="text-base mb-3"
                  style={{
                    fontFamily: "'Noto Serif JP', serif",
                    fontWeight: 500,
                    color: '#1A1A1A',
                  }}
                >
                  {r.title}
                </h3>
                <p
                  className="text-sm leading-relaxed"
                  style={{
                    color: '#7A7569',
                    fontFamily: "'Noto Serif JP', serif",
                  }}
                >
                  {r.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Open Positions */}
        <section ref={positionsRef as React.RefObject<HTMLElement>}>
          <div className="mb-16">
            <ElegantSectionTitle
              tag="Open Positions"
              title={t('recruitment.positionsTitle')}
            />
          </div>
          <div className="space-y-4">
            {positions.map((pos, i) => (
              <div
                key={i}
                className="p-6 md:p-8 border transition-all duration-300"
                style={{
                  background: '#FFFFFF',
                  borderColor: 'rgba(212, 207, 196, 0.4)',
                  opacity: positionsVisible ? 1 : 0,
                  transform: positionsVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.6s ease ${i * 0.08}s, transform 0.6s ease ${i * 0.08}s, border-color 0.3s`,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(201, 162, 39, 0.3)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(212, 207, 196, 0.4)';
                }}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div>
                    <h3
                      className="text-lg mb-2"
                      style={{
                        fontFamily: "'Noto Serif JP', serif",
                        fontWeight: 500,
                        color: '#1A1A1A',
                      }}
                    >
                      {pos.title}
                    </h3>
                    <div className="flex items-center gap-4 text-xs" style={{ color: '#A8A294', fontFamily: "'Inter', sans-serif" }}>
                      <span className="flex items-center gap-1.5">
                        <i className="ri-briefcase-line" />
                        {pos.type}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <i className="ri-map-pin-line" />
                        {pos.location}
                      </span>
                    </div>
                    <p
                      className="text-sm mt-3"
                      style={{
                        color: '#7A7569',
                        fontFamily: "'Noto Serif JP', serif",
                      }}
                    >
                      {pos.description}
                    </p>
                  </div>
                  <Link
                    to="/contact"
                    className="shrink-0 inline-flex items-center gap-2 whitespace-nowrap cursor-pointer no-underline text-xs tracking-wider uppercase transition-all duration-300"
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 500,
                      padding: '10px 24px',
                      border: '1px solid #C9A227',
                      color: '#C9A227',
                      background: 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.background = '#C9A227';
                      (e.currentTarget as HTMLAnchorElement).style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
                      (e.currentTarget as HTMLAnchorElement).style.color = '#C9A227';
                    }}
                  >
                    {t('recruitment.apply')}
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Application Process */}
        <section ref={processRef as React.RefObject<HTMLElement>}>
          <div className="mb-16">
            <ElegantSectionTitle
              tag="Process"
              title={t('recruitment.processTitle')}
            />
          </div>
          <div className="relative max-w-3xl mx-auto">
            <div
              className="absolute left-[23px] top-0 bottom-0 w-px hidden md:block"
              style={{ background: 'linear-gradient(180deg, rgba(201,162,39,0.2), rgba(201,162,39,0.1))' }}
            />
            <div className="space-y-6">
              {steps.map((step, i) => (
                <div
                  key={i}
                  className="flex gap-5 items-start"
                  style={{
                    opacity: processVisible ? 1 : 0,
                    transform: processVisible ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `opacity 0.65s ease ${i * 0.1}s, transform 0.65s ease ${i * 0.1}s`,
                  }}
                >
                  <div
                    className="flex-shrink-0 w-12 h-12 flex items-center justify-center text-sm font-medium z-10"
                    style={{
                      background: '#C9A227',
                      color: '#0A192F',
                      fontFamily: "'Playfair Display', serif",
                    }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div
                    className="flex-1 p-6 border"
                    style={{
                      background: '#FFFFFF',
                      borderColor: 'rgba(212, 207, 196, 0.4)',
                    }}
                  >
                    <h3
                      className="text-base mb-2"
                      style={{
                        fontFamily: "'Noto Serif JP', serif",
                        fontWeight: 500,
                        color: '#1A1A1A',
                      }}
                    >
                      {step.title}
                    </h3>
                    <p
                      className="text-sm leading-relaxed"
                      style={{
                        color: '#7A7569',
                        fontFamily: "'Noto Serif JP', serif",
                      }}
                    >
                      {step.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <ElegantFooter />
    </div>
  );
}
